import React from "react";

interface TableProps {
  children: React.ReactNode;
  className?: string;
}

export function Table({ children, className = "" }: TableProps) {
  return (
    <div className={`overflow-x-auto bg-card rounded-lg shadow border border-border ${className}`}>
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  );
}

interface TableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export function TableHeader({ children, className = "" }: TableHeaderProps) {
  return (
    <thead className={className}>
      <tr className="bg-muted text-muted-foreground">{children}</tr>
    </thead>
  );
}

interface TableBodyProps {
  children: React.ReactNode;
  className?: string;
}

export function TableBody({ children, className = "" }: TableBodyProps) {
  return <tbody className={className}>{children}</tbody>;
}

interface TableRowProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  clickable?: boolean;
}

export function TableRow({
  children,
  className = "",
  onClick,
  clickable,
}: TableRowProps) {
  const isClickable = clickable !== undefined ? clickable : !!onClick;
  const clickableClasses = isClickable
    ? "cursor-pointer hover:bg-accent transition-colors"
    : "";

  return (
    <tr className={`${clickableClasses} ${className}`} onClick={onClick}>
      {children}
    </tr>
  );
}

interface TableCellProps {
  children: React.ReactNode;
  className?: string;
  align?: "left" | "right" | "center";
  colSpan?: number;
  variant?: "header" | "body" | "default";
  nowrap?: boolean;
}

export function TableCell({
  children,
  className = "",
  align = "left",
  colSpan,
  variant = "default",
  nowrap = false,
}: TableCellProps) {
  const alignClass = align === "right" ? "text-right" : align === "center" ? "text-center" : "";
  
  const variantClasses = {
    header: "px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider",
    body: "px-6 py-4 text-sm text-foreground",
    default: "",
  };

  const nowrapClass = nowrap ? "whitespace-nowrap" : "";

  return (
    <td
      className={`${alignClass} ${variantClasses[variant]} ${nowrapClass} ${className}`}
      colSpan={colSpan}
    >
      {children}
    </td>
  );
}

